import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SegmentOutIntegration as UIAPISegmentOutIntegration } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { clearSegmentOutIntegrationCache, setSegmentOutIntegrationCache } from './cache';
import { parseSegmentOutIntegration, type SegmentOutIntegration, type SegmentOutSettingsInput } from './entity';

/**
 * Segment source connector writes: connect (POST), save settings (PUT),
 * disconnect (DELETE).
 *
 * Each hook writes the canonical response into the single detail slot the
 * directory and connections overview also read.
 *
 * Connect and save are not two spellings of one write. Connect carries the write
 * key and `use_javascript`, which is what marks the record as the current
 * generation; save carries every setting and no key, because the key is
 * create-only.
 */
function requireSegmentOut(res: { segment_out?: UIAPISegmentOutIntegration }): UIAPISegmentOutIntegration {
  if (res.segment_out == null) {
    throw new Error('Segment source response did not include a segment_out record');
  }
  return res.segment_out;
}

/* ------------------------------------------------------------------------- */
/* connectSegmentOutIntegration                                              */
/* ------------------------------------------------------------------------- */

export interface ConnectSegmentOutIntegrationInput {
  workspaceId: number;
  key: string;
}

/**
 * @public Connect the workspace to Segment with a write key.
 *
 * `use_javascript: true` is sent explicitly rather than left to the backend
 * default: it is what distinguishes a record configured here from a legacy one
 * served by the `segdotcom` endpoint, and the connect form is the only place it
 * is ever set.
 */
export async function connectSegmentOutIntegration({
  workspaceId,
  key,
}: ConnectSegmentOutIntegrationInput): Promise<SegmentOutIntegration> {
  const data = await hydraUiApi.integrations.createSegmentOut({
    params: { path: { environment_id: String(workspaceId) } },
    body: { segment_out: { key, use_javascript: true } },
  });

  return parseSegmentOutIntegration(requireSegmentOut(data));
}

/** What the hook's caller passes, with the workspace supplied by the hook. */
export type UseConnectSegmentOutVariables = Omit<ConnectSegmentOutIntegrationInput, 'workspaceId'>;

type ConnectOptions = {
  mutationConfig?: HookMutationConfig<SegmentOutIntegration, UseConnectSegmentOutVariables>;
};

/** @public */
export const useConnectSegmentOutIntegration = ({ mutationConfig }: ConnectOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseConnectSegmentOutVariables) => connectSegmentOutIntegration({ ...input, workspaceId }),
    onSuccess: (integration, ...args) => {
      setSegmentOutIntegrationCache(qc, workspaceId, integration);
      onSuccess?.(integration, ...args);
    },
    ...rest,
  });
};

/* ------------------------------------------------------------------------- */
/* saveSegmentOutIntegration                                                 */
/* ------------------------------------------------------------------------- */

export interface SaveSegmentOutIntegrationInput extends SegmentOutSettingsInput {
  workspaceId: number;
  integrationId: number;
}

/**
 * @public Save the connector's settings.
 *
 * The backend requires every setting on update, not just the ones this form
 * edits, so the caller sends the record's current values for the rest. Sending
 * them verbatim is deliberate: deciding any of them here would silently rewrite
 * a field the reader never touched.
 */
export async function saveSegmentOutIntegration({
  workspaceId,
  integrationId,
  ...settings
}: SaveSegmentOutIntegrationInput): Promise<SegmentOutIntegration> {
  const data = await hydraUiApi.integrations.updateSegmentOut({
    params: { path: { environment_id: String(workspaceId), id: String(integrationId) } },
    body: { segment_out: settings },
  });

  return parseSegmentOutIntegration(requireSegmentOut(data));
}

export type UseSaveSegmentOutVariables = Omit<SaveSegmentOutIntegrationInput, 'workspaceId'>;

type SaveOptions = {
  mutationConfig?: HookMutationConfig<SegmentOutIntegration, UseSaveSegmentOutVariables>;
};

/** @public */
export const useSaveSegmentOutIntegration = ({ mutationConfig }: SaveOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseSaveSegmentOutVariables) => saveSegmentOutIntegration({ ...input, workspaceId }),
    onSuccess: (integration, ...args) => {
      setSegmentOutIntegrationCache(qc, workspaceId, integration);
      onSuccess?.(integration, ...args);
    },
    ...rest,
  });
};

/* ------------------------------------------------------------------------- */
/* disconnectSegmentOutIntegration                                           */
/* ------------------------------------------------------------------------- */

export interface DisconnectSegmentOutIntegrationInput {
  workspaceId: number;
  integrationId: number;
}

/** @public Remove the connector. */
export async function disconnectSegmentOutIntegration({
  workspaceId,
  integrationId,
}: DisconnectSegmentOutIntegrationInput): Promise<void> {
  await hydraUiApi.integrations.deleteSegmentOut({
    params: { path: { environment_id: String(workspaceId), id: String(integrationId) } },
  });
}

export type UseDisconnectSegmentOutVariables = Omit<DisconnectSegmentOutIntegrationInput, 'workspaceId'>;

type DisconnectOptions = {
  mutationConfig?: HookMutationConfig<void, UseDisconnectSegmentOutVariables>;
};

/** @public */
export const useDisconnectSegmentOutIntegration = ({ mutationConfig }: DisconnectOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseDisconnectSegmentOutVariables) => disconnectSegmentOutIntegration({ ...input, workspaceId }),
    onSuccess: (result, ...args) => {
      clearSegmentOutIntegrationCache(qc, workspaceId);
      onSuccess?.(result, ...args);
    },
    ...rest,
  });
};
